"use client";
import { useEffect, useRef } from "react";

/**
 * 親が「この実行はもう終わった」と言ってから、観測を畳むまでの猶予。
 *
 * useAICheckRun 側の ABORT_OBSERVE_GRACE_MS と同じ理由による保険で、
 * 経路が違うだけ（あちらは Abort を押した本人、こちらは親から
 * 観測対象を落とされた側）。
 */
export const OBSERVE_DETACH_GRACE_MS = 15_000;

interface GracefulObservationOptions {
  /** 観測する実行。null なら観測しない（＝畳む側に回る）。 */
  runId: string | null;
  /**
   * runId 以外に張り替えの契機となる値をまとめた鍵（言語・案件など）。
   * 変わったら今の観測を畳んで張り直す。
   */
  scopeKey: string;
  /** 観測を始める。**identity は問わない**（ref 経由で最新を読む）。 */
  start: (signal: AbortSignal) => Promise<unknown>;
  /** 中断以外の失敗の行き先。 */
  onError?: (error: unknown) => void;
  graceMs?: number;
}

/**
 * 実行の観測を張り、**畳むときだけ猶予を置く**フック。
 *
 * **中断した直後に自分の観測を切ってはいけない。** Abort を押すと状態は
 * 先に "aborted" になり、親はそれを実行終了とみなして観測対象を落とす。
 * そこで即座にストリームを閉じると、サーバがそのあと配る中断状態
 * （実行中だったノードの aborted）が押した本人にだけ届かず、グラフは
 * オレンジのまま点滅し続ける（別のブラウザで開くと正しく中断済みで見える）。
 * 終端イベントが来ればストリームはそこで閉じるので、猶予は
 * 「サーバが応えなかったとき接続を抱えたままにしない」ための保険。
 *
 * 一方で**張り替えとアンマウントは即座に畳む** — 前者は同じ実行へ二本
 * 繋がるのを防ぐため、後者は見ていない接続を抱え続けないため。
 */
export function useGracefulObservation({
  runId,
  scopeKey,
  start,
  onError,
  graceMs = OBSERVE_DETACH_GRACE_MS,
}: GracefulObservationOptions): void {
  const startRef = useRef(start);
  startRef.current = start;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const unmountedRef = useRef(false);
  // **この effect は下の観測 effect より先に宣言する。** React は後片付けを
  // 宣言順に呼ぶので、アンマウント時はこちらが先に立つ。
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const previousRef = useRef<AbortController | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scopeKey は本体では読まない「張り替えの契機」そのもの。外すと言語や案件を変えても観測が付け替わらない
  useEffect(() => {
    if (!runId) return;

    // 張り替えのときは前の観測をここで確実に閉じる
    // （後片付けは猶予付きなので、そちらには任せられない）。
    previousRef.current?.abort();
    const controller = new AbortController();
    previousRef.current = controller;

    startRef.current(controller.signal).catch((error: unknown) => {
      // 中断は畳んだ結果であって障害ではない。握り潰さないと
      // 「Uncaught (in promise) AbortError」として上がってくる。
      if (error instanceof DOMException && error.name === "AbortError") return;
      onErrorRef.current?.(error);
    });

    return () => {
      if (unmountedRef.current) {
        controller.abort();
        return;
      }
      const timer = setTimeout(() => controller.abort(), graceMs);
      // 張り替えで先に abort された場合はタイマーも落とす。
      controller.signal.addEventListener("abort", () => clearTimeout(timer));
    };
  }, [runId, scopeKey, graceMs]);
}
